import React, { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router';
import css from './animal-list.module.css';
import Table from '../../../../components/common/table/table';
import Animal from '../../../../interfaces/animal';
import { ListParams } from '../../../../services/http-client';
import { useService } from '../../../../services/services';
import Toolbar from './components/toolbar';
import { can, route } from '../../../../services/utils';
import { LIVESTOCK_ANIMALS_VIEW } from '../../../../router/routes';
import TableVerticalSizeFixer from '../../../../components/common/table/table-vertical-size-fixer/table-vertical-size-fixer';
import { useSelector } from 'react-redux';
import { RootState } from '../../../../store/store';

export default function AnimalsList() {
  const { t } = useTranslation();
  const transKeyPrefix = 'pages.livestock.animals_and_events.animals.';
  const tp = useCallback((key: string) => t(transKeyPrefix.concat(key)), [t]);
  const { farmId } = useParams<{ farmId: string }>();
  const { animalService } = useService();
  const [selectedAnimals, setSelectedAnimals] = useState<Animal[]>([]);

  const { farmUser } = useSelector((state: RootState) => ({
    farmUser: state.global.farmUser,
  }));

  const request = useCallback(
    (page: number, sort: string, desc: boolean, filter: any) =>
      animalService.index(farmId, { page, sort, desc, ...filter }, true),
    [animalService, farmId]
  );

  const viewRoute = useCallback(
    (animal: Animal) =>
      route(LIVESTOCK_ANIMALS_VIEW, {
        farmId,
        id: animal.animal_main_id,
      }),
    [farmId]
  );

  const rowDisabled = (animal: Animal) => {
    return !animal.animal_main_id;
  }

  const rowCheckable = (animal: Animal) => {
    return (!animal.exclusion_date && !animal.moved);
  }

  const toolbar = useCallback(
    (params: ListParams): JSX.Element => {
      const animals = selectedAnimals.map((el) => el.animal_main_id);

      return <Toolbar params={{ animals, ...params }} />;
    },
    [selectedAnimals]
  );

  return (
    <div className={css['body']}>
      <TableVerticalSizeFixer>
        <Table<Animal>
          request={request}
          keyAccessor="id"
          toolbar={toolbar}
          paginate={false}
          columns={[
            {
              accessor: 'animal_id',
              header: tp('animal_id'),
              sortable: true,
              className: css['strong'],
            },
            {
              accessor: 'name',
              header: tp('name'),
              sortable: true,
              className: css['strong'],
            },
            {
              accessor: 'gender',
              header: tp('gender'),
              sortable: true,
              className: css['normal'],
            },
            {
              accessor: 'age',
              header: tp('age'),
              sortable: true,
              className: css['normal'],
              valuable: (value) => {
                if (value === '0') {
                  return value;
                } else {
                  const dateValues = value.split(' ');
                  const dateElement = ['g. ', 'mēn. ', ' d.'];
                  let date = '';
                  for (let index = 0; index < dateElement.length; index++) {
                    if (dateValues[index] !== '0') {
                      date += dateValues[index] + dateElement[index];
                    }
                  }
                  return date;
                }
              },
            },
            {
              accessor: 'breed_code',
              header: tp('breed'),
              sortable: true,
              className: css['normal'],
            },
            {
              accessor: 'categories_concatenated',
              header: tp('animal_group'),
              sortable: true,
              className: css['normal'],
            },
            {
              accessor: 'barn_ldc_nr',
              header: tp('barn'),
              sortable: true,
              className: css['normal'],
            },
            {
              accessor: 'herd_id',
              header: tp('herd'),
              sortable: true,
              className: css['normal'],
            },
            {
              accessor: 'exclusion_date',
              header: tp('exclusion_date'),
              className: css['normal'],
            },
            {
              accessor: 'exclusion_type',
              header: tp('exclusion_type'),
              className: css['normal'],
            },
          ]}
          viewRoute={viewRoute}
          viewRecordAbility={{ title: 'view animal' }}
          selectedRowsState={
            can(farmUser, { title: 'create animal_event' })
              ? [selectedAnimals, setSelectedAnimals]
              : null
          }
          filteredTotalsParams={{
            recordName: 'pages.livestock.animals_and_events.animals.animals',
          }}
          rowDisabled={rowDisabled}
          rowCheckable={rowCheckable}
        />
      </TableVerticalSizeFixer>
    </div>
  );
}
